import { Modal } from '@sgcmpteltd/uikit-next';
import { useLatest } from '@sgcmpteltd/uikit-react';
import type { Size } from '@sgcmpteltd/uikit-react/types';
import cx from 'clsx';
import dayjs from 'dayjs';
import Image from 'next/image';
import { useEffect, useState } from 'react';

import type { OnRemainSecondsChange } from '@/hooks/use-count-down';
import { useCountDown } from '@/hooks/use-count-down';

import {
  BACKDROP_TRANSITION_DURATION_MS,
  MODAL_CLOSE_COUNTDOWN_SEC,
} from './constants';
import styles from './styles.module.scss';
import type { Component, OnModalClose } from './types';

export const PtoAdModal: Component = ({
  adImageSrc,
  adLink,
  isOpen,
  backdropTransitionDurationMs = BACKDROP_TRANSITION_DURATION_MS,
  onClose,
}) => {
  const [
    countdownTargetTimestamp,
    setCountdownTargetTimestamp,
  ] = useState<number | null>(null);
  const [adImageSize, setAdImageSize] = useState<Size | null>(null);

  if (isOpen && countdownTargetTimestamp === null) {
    setCountdownTargetTimestamp(
      +dayjs().add(MODAL_CLOSE_COUNTDOWN_SEC, 'seconds'),
    );
  }

  const onModalClose: OnModalClose = () => {
    setCountdownTargetTimestamp(0);
    onClose?.();
  };

  const {
    ref: onRemainSecondsChangeRef,
  } = useLatest<OnRemainSecondsChange>({
    value: (remainSeconds) => {
      if (remainSeconds > 0 || !isOpen) {
        return;
      }
      onModalClose();
    },
  });

  const {
    remainSeconds: modalCloseRemainSeconds,
  } = useCountDown({
    targetTimestamp: countdownTargetTimestamp ?? 0,
    onRemainSecondsChangeRef,
  });

  useEffect(() => {
    const image = new window.Image();

    image.src = adImageSrc;

    image.onload = ({ target }) => {
      if (!(target instanceof HTMLImageElement)) {
        return;
      }

      const {
        naturalWidth,
        naturalHeight,
      } = target;

      setAdImageSize({
        width: naturalWidth,
        height: naturalHeight,
      });
    };
  }, [adImageSrc]);

  return (
    <Modal
      backdropTransitionDurationMs={backdropTransitionDurationMs}
      className={styles.containerRoot}
      contentClassName={styles.containerModalContent}
      headerClassName={styles.containerModalHeader}
      isOpen={isOpen}
      title={null}
      isContentFull
      onClose={onModalClose}>
      <div
        className={styles.containerAdContent}>
        <div
          className={styles.containerAdContentHeader}>
          <span
            className={cx(
              styles.textModalCloseCountdown,
              {
                [styles.isHidden]: (modalCloseRemainSeconds ?? 0) <= 0,
              },
            )}>
            {modalCloseRemainSeconds}
          </span>
          <button
            className={styles.buttonSkipAd}
            onClick={() => {
              onModalClose();
            }}>
            Skip Ad
          </button>
        </div>
        <div
          className={styles.containerAdContentLink}>
          <a
            className={styles.linkAdContent}
            href={adLink}
            rel="noreferrer noopener"
            target="_blank">
            {
              adImageSize && (
                <Image
                  alt="PTO ads image"
                  className={styles.imagePtoAds}
                  height={adImageSize.height}
                  src={adImageSrc}
                  width={adImageSize.width} />
              )
            }
          </a>
        </div>
      </div>
    </Modal>
  );
};
